import { useState, useRef, useEffect } from "react";
import getTextColor from "@/utils/getTextColor";
import {
  EMAIL_VALIDATION_MESSAGE,
  OTP_PLACEHOLDER_MESSAGE,
  PHONE_VALIDATION_MESSAGE,
  SENDING_OTP_MESSAGE,
  RESEND_OTP_MESSAGE,
} from "@/language.constants";
import deriveShade from "@/utils/deriveShade";

interface OTPCollectionProps {
  chatConfig: any;
  userContact: string;
  onVerify: (otp: string) => void;
  onResend: (contact: string) => void; // Changed to accept contact parameter
  type?: "email" | "phone" | undefined;
  language: string;
  isLast?: boolean;
  isOverLimit?: boolean;
  isResendMessage?: boolean;
}

export const OTPCollection = ({
  chatConfig,
  userContact,
  onVerify,
  onResend,
  type = "email",
  language,
  isLast,
  isOverLimit,
  isResendMessage,
}: OTPCollectionProps) => {
  const [otp, setOtp] = useState("");
  const [contact, setContact] = useState(userContact);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userContact && userContact.trim()) {
      setContact(userContact);
    }
  }, [userContact]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
      setError("");
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact(e.target.value);
    setError("");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^d+]/g, "");
    if (value.includes("+")) {
      const parts = value.split("+");
      value =
        "+" +
        parts
          .filter((p) => p)
          .join("")
          .replace(/\D/g, "");
    }
    setContact(value);
    setError("");
  };

  const handleEditEmail = () => {
    setIsEditingEmail(true);
    setTimeout(() => {
      emailInputRef.current?.focus();
      emailInputRef.current?.select();
    }, 0);
  };

  const handleEditPhone = () => {
    setIsEditingPhone(true);
    setTimeout(() => {
      phoneInputRef.current?.focus();
      phoneInputRef.current?.select();
    }, 0);
  };
  const handleEmailBlur = () => {
    setIsEditingEmail(false);
    if (
      !contact ||
      !contact.includes("@") ||
      !contact.includes(".") ||
      contact[contact.length - 1] === "."
    ) {
      setError(EMAIL_VALIDATION_MESSAGE[language?.toLowerCase()]);
    }
  };
  const handlePhoneBlur = () => {
    setIsEditingPhone(false);
    if (!contact || contact.length < 11 || !contact.startsWith("+")) {
      setError(PHONE_VALIDATION_MESSAGE[language?.toLowerCase()]);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsVerifying(true);
    try {
      await onVerify(otp);
    } catch (err) {
      setError("Invalid OTP. Please try again.");
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (
      type === "email" &&
      (!contact ||
        !contact.includes("@") ||
        !contact.includes(".") ||
        contact[contact.length - 1] === ".")
    ) {
      setError(EMAIL_VALIDATION_MESSAGE[language?.toLowerCase()]);
      return;
    }
    if (
      type === "phone" &&
      (!contact || !contact.includes("+") || contact.length < 10)
    ) {
      setError(PHONE_VALIDATION_MESSAGE[language?.toLowerCase()]);
      return;
    }

    setIsResending(true);
    try {
      await onResend(contact);
      setOtp("");
      setError("");
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleNewEmailChange = async () => {
    if (
      type === "email" &&
      (!contact ||
        !contact.includes("@") ||
        !contact.includes(".") ||
        contact[contact.length - 1] === ".")
    ) {
      setError(EMAIL_VALIDATION_MESSAGE[language?.toLowerCase()]);
      return;
    }

    setIsResending(true);
    try {
      await onResend(contact);
      setOtp("");
      setError("");
      setIsEditingEmail(false);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleNewPhoneChange = async () => {
    if (
      type === "phone" &&
      (!contact || !contact.includes("+") || contact.length < 10)
    ) {
      setError(PHONE_VALIDATION_MESSAGE[language?.toLowerCase()]);
      return;
    }
    setIsResending(true);
    try {
      await onResend(contact);
      setOtp("");
      setError("");
      setIsEditingPhone(false);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && otp.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="cbb-bot-msg-wrapper adjusted-width botleft cbb-otp-email-section-wrapper w-full">
      <div
        className="cbb-bot-msg-box botChatBox !w-full"
        style={{
          background: chatConfig?.botBubbleColor,
        }}
      >
        {/* Email Section */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span
              className="text-xs font-medium"
              style={{ color: chatConfig?.typography?.color }}
            >
              {!isResendMessage
                ? SENDING_OTP_MESSAGE[language?.toLowerCase()]
                : ""}
            </span>
          </div>

          <div
            className="group relative rounded-lg px-[10px] py-[7px] transition-all duration-200"
            style={{
              background:
                isEditingEmail || isEditingPhone
                  ? chatConfig?.typography?.color
                  : deriveShade(chatConfig?.botBubbleColor, 0.1),
            }}
          >
            <div className="flex items-center gap-2">
              <svg
                className="flex-shrink-0 opacity-70"
                width={16}
                height={16}
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6.66669L9.0755 11.0504C9.63533 11.4236 10.3647 11.4236 10.9245 11.0504L17.5 6.66669M4.16667 15.8334H15.8333C16.7538 15.8334 17.5 15.0872 17.5 14.1667V5.83335C17.5 4.91288 16.7538 4.16669 15.8333 4.16669H4.16667C3.24619 4.16669 2.5 4.91288 2.5 5.83335V14.1667C2.5 15.0872 3.24619 15.8334 4.16667 15.8334Z"
                  stroke={
                    isEditingEmail || isEditingPhone
                      ? getTextColor(chatConfig?.typography?.color)
                      : chatConfig?.typography?.color
                  }
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <input
                ref={type === "email" ? emailInputRef : phoneInputRef}
                type="text"
                inputMode={type === "email" ? "email" : "tel"}
                value={contact}
                onChange={
                  type === "email" ? handleEmailChange : handlePhoneChange
                }
                onBlur={type === "email" ? handleEmailBlur : handlePhoneBlur}
                placeholder={
                  type === "email"
                    ? EMAIL_VALIDATION_MESSAGE[language?.toLowerCase()]
                    : PHONE_VALIDATION_MESSAGE[language?.toLowerCase()]
                }
                readOnly={!(isEditingEmail || isEditingPhone)}
                className="!h-[unset] flex-1 truncate !border-none !bg-transparent !p-0 text-sm font-medium !outline-none"
                style={{
                  color:
                    isEditingEmail || isEditingPhone
                      ? getTextColor(chatConfig?.typography?.color)
                      : chatConfig?.typography?.color,
                  cursor: isEditingEmail || isEditingPhone ? "text" : "default",
                }}
              />

              <button
                onClick={type === "email" ? handleEditEmail : handleEditPhone}
                className="grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-md transition-all duration-200 hover:scale-110 active:scale-95"
                style={{
                  background:
                    isEditingEmail || isEditingPhone
                      ? "transparent"
                      : "rgba(255,255,255,0.2)",
                }}
                title={type === "email" ? "Edit email" : "Edit phone number"}
              >
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.586 0.586C11.367 -0.195 12.633 -0.195 13.414 0.586C14.195 1.367 14.195 2.633 13.414 3.414L12.621 4.207L9.793 1.379L10.586 0.586ZM8.379 2.793L0 11.172V14H2.828L11.207 5.621L8.379 2.793Z"
                    fill={
                      isEditingEmail || isEditingPhone
                        ? chatConfig?.typography?.color
                        : chatConfig?.typography?.color
                    }
                    opacity={isEditingEmail || isEditingPhone ? 0.5 : 0.8}
                  />
                </svg>
              </button>

              {(isEditingEmail || isEditingPhone) && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={
                    type === "email"
                      ? handleNewEmailChange
                      : handleNewPhoneChange
                  }
                  disabled={
                    isLast ||
                    isOverLimit ||
                    contact === userContact ||
                    !contact.includes("@")
                  }
                  className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                  style={{
                    background: chatConfig?.botBubbleColor || "#2C81FE",
                  }}
                >
                  <svg
                    className="ml-0.5"
                    width={11}
                    height={11}
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.49526 0.0358542L11.0405 5.30383C11.2106 5.38881 11.2806 5.59752 11.1968 5.76999C11.1633 5.8388 11.1084 5.89449 11.0405 5.92839L0.495477 11.1963C0.325358 11.2812 0.119494 11.2103 0.0356668 11.0379C-0.000988388 10.9624 -0.00948539 10.8761 0.0117452 10.7949L1.05652 6.79499C1.09107 6.66271 1.19886 6.56322 1.332 6.54071L6.05775 5.74188C6.11564 5.7321 6.16351 5.69335 6.18652 5.64068L6.19888 5.5988C6.21224 5.51752 6.16773 5.44001 6.09583 5.40958L6.05775 5.39848L1.30531 4.59547C1.17212 4.57296 1.06431 4.47341 1.02979 4.34107L0.0115072 0.437181C-0.037001 0.251307 0.0723037 0.0607588 0.255646 0.0115807C0.335789 -0.00991606 0.420898 -0.00129437 0.49526 0.0358542Z"
                      fill={chatConfig?.typography?.color}
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {error && (
            <div
              className="mt-2 text-[12px] font-medium italic"
              style={{
                color: chatConfig?.typography?.color,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {!isResendMessage && (
            <>
              <div
                className="rounded-lg px-[10px] py-[7px]"
                style={{ background: "rgba(255,255,255,0.95)" }}
              >
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={handleOtpChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      OTP_PLACEHOLDER_MESSAGE[language?.toLowerCase()]
                    }
                    readOnly={isLast || isOverLimit}
                    className="!h-[unset] flex-1 !border-none !bg-transparent !p-0 font-mono text-sm tracking-wider !outline-none"
                    style={{
                      color: getTextColor(chatConfig?.typography?.color),
                    }}
                  />

                  <button
                    onClick={handleVerify}
                    disabled={
                      otp.length !== 6 || isVerifying || isLast || isOverLimit
                    }
                    className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                    style={{
                      background: chatConfig?.botBubbleColor || "#2C81FE",
                    }}
                  >
                    <svg
                      className="ml-0.5"
                      width={11}
                      height={11}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.49526 0.0358542L11.0405 5.30383C11.2106 5.38881 11.2806 5.59752 11.1968 5.76999C11.1633 5.8388 11.1084 5.89449 11.0405 5.92839L0.495477 11.1963C0.325358 11.2812 0.119494 11.2103 0.0356668 11.0379C-0.000988388 10.9624 -0.00948539 10.8761 0.0117452 10.7949L1.05652 6.79499C1.09107 6.66271 1.19886 6.56322 1.332 6.54071L6.05775 5.74188C6.11564 5.7321 6.16351 5.69335 6.18652 5.64068L6.19888 5.5988C6.21224 5.51752 6.16773 5.44001 6.09583 5.40958L6.05775 5.39848L1.30531 4.59547C1.17212 4.57296 1.06431 4.47341 1.02979 4.34107L0.0115072 0.437181C-0.037001 0.251307 0.0723037 0.0607588 0.255646 0.0115807C0.335789 -0.00991606 0.420898 -0.00129437 0.49526 0.0358542Z"
                        fill={chatConfig?.typography?.color}
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="flex w-full !justify-end">
            <button
              onClick={handleResend}
              disabled={
                isResending ||
                isLast ||
                isOverLimit ||
                !contact ||
                (!contact.includes("@") &&
                  (!contact.includes(".") || !contact.includes("+")))
              }
              className="text-xs font-medium underline transition-all duration-200 hover:opacity-80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
              style={{ color: chatConfig?.typography?.color }}
            >
              {isResending
                ? "Sending..."
                : RESEND_OTP_MESSAGE[language?.toLowerCase()]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
